import classNames from "classnames";
import { JSXElementConstructor, memo } from "react";
import * as iconCss from "./redo-icon.module.css";

export const iconPickerColor = [
  "lightGrey",
  "grey",
  "darkGrey",
  "blue",
  "green",
  "yellow",
  "orange",
  "red",
  "pink",
  "purple",
] as const;

const redoIconColor = [
  "primary",
  "secondary",
  "tertiary",
  "quaternary",
  "quinary",
  "disabled",
  "disabled-subtle",
  "error",
  "warning",
  "success",
  "inherit",
  "brand",
  "white",
] as const;
export type RedoIconColor =
  | (typeof redoIconColor)[number]
  | (typeof iconPickerColor)[number];

export function getColorClassName(color: RedoIconColor): string {
  return colorToCss[color];
}

export function getBackgroundColorStyle(color: RedoIconColor): string {
  return colorToBackground[color] || colorToBackground.grey;
}

export interface RedoIconProps {
  color?: RedoIconColor;
  size?: number;
  Icon: JSXElementConstructor<any>;
  className?: string;
  withBackground?: boolean;
}

export const RedoIcon = memo(function RedoIcon({
  color = "inherit",
  size = 12,
  Icon,
  className,
  withBackground = false,
}: RedoIconProps) {
  if (withBackground) {
    const backgroundSize = size * 2;
    return (
      <div
        style={{
          backgroundColor: getBackgroundColorStyle(color),
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: `${backgroundSize}px`,
          width: `${backgroundSize}px`,
        }}
      >
        <Icon
          className={classNames(
            iconCss.icon,
            getColorClassName(color),
            className,
          )}
          style={{ height: `${size}px`, width: `${size}px` }}
        />
      </div>
    );
  }

  return (
    <Icon
      className={classNames(iconCss.icon, getColorClassName(color), className)}
      style={{ height: `${size}px`, width: `${size}px` }}
    />
  );
});

const colorToBackground: Record<RedoIconColor, string> = {
  // Standard icon colors - using light background colors
  primary: "var(--redo-colors-gray-light-mode-100)",
  secondary: "var(--redo-colors-gray-light-mode-100)",
  tertiary: "var(--redo-colors-gray-light-mode-100)",
  quaternary: "var(--redo-colors-gray-light-mode-100)",
  quinary: "var(--redo-colors-gray-light-mode-100)",
  disabled: "var(--redo-colors-gray-light-mode-100)",
  "disabled-subtle": "var(--redo-colors-gray-light-mode-100)",
  error: "var(--redo-colors-error-100)",
  warning: "var(--redo-neutral-yellow-color)",
  success: "var(--redo-colors-success-100)",
  inherit: "var(--redo-colors-gray-light-mode-100)",
  brand: "var(--redo-colors-gray-light-mode-100)",
  // Icon picker colors - light backgrounds
  lightGrey: "var(--redo-colors-gray-light-mode-50)",
  grey: "var(--redo-colors-gray-light-mode-200)",
  darkGrey: "var(--redo-colors-gray-light-mode-300)",
  blue: "var(--redo-colors-blue-light-50)",
  green: "var(--redo-colors-success-100)",
  yellow: "var(--redo-neutral-yellow-color)",
  orange: "var(--redo-colors-orange-100)",
  red: "var(--redo-colors-error-100)",
  pink: "var(--redo-colors-pink-100)",
  purple: "var(--redo-colors-purple-100)",
  white: "var(--redo-colors-base-white)",
};

const colorToCss: Record<RedoIconColor, string> = {
  primary: iconCss.primary,
  secondary: iconCss.secondary,
  tertiary: iconCss.tertiary,
  quaternary: iconCss.quaternary,
  quinary: iconCss.quinary,
  disabled: iconCss.disabled,
  "disabled-subtle": iconCss.disabledSubtle,
  error: iconCss.error,
  warning: iconCss.warning,
  success: iconCss.success,
  inherit: iconCss.inherit,
  brand: iconCss.brand,
  lightGrey: iconCss.lightGrey,
  grey: iconCss.grey,
  darkGrey: iconCss.darkGrey,
  blue: iconCss.blue,
  green: iconCss.green,
  yellow: iconCss.yellow,
  orange: iconCss.orange,
  red: iconCss.red,
  pink: iconCss.pink,
  purple: iconCss.purple,
  white: iconCss.white,
};
